import { objectify, stringify } from '#library/serialize.library.js';
import { CONTEXT, getContext } from '#library/utility.library.js';
import { isDefined, isUndefined, isString } from '#library/assertion.library.js';
const context = getContext();
/** simplified in-memory storage for restricted browser contexts */
const mockStorage = {
    length: 0,
    clear: () => { },
    getItem: (_key) => null,
    key: (_index) => null,
    removeItem: (_key) => { },
    setItem: (_key, _value) => { },
};
/** safely attempt to retrieve a Storage object from the global context */
const getSafeStorage = (name = 'localStorage') => {
    try {
        return context.global?.[name] ?? mockStorage;
    }
    catch {
        return mockStorage;
    }
};
let storage = context.type === CONTEXT.Browser
    ? getSafeStorage()
    : mockStorage;
/**
 * Selects the active browser storage mechanism (localStorage or sessionStorage).
 *
 * @param store - The storage type to use (default: 'local')
 * @returns The selected Storage object
 */
export function selStorage(store = 'local') {
    const name = (store + 'Storage');
    return storage = getSafeStorage(name);
}
export function getStorage(key, dflt) {
    let store;
    if (isUndefined(key))
        return dflt ?? {};
    switch (context.type) {
        case CONTEXT.Browser:
            store = storage.getItem(key);
            break;
        case CONTEXT.NodeJS:
            store = context.global.process.env[key];
            break;
        case CONTEXT.Deno:
            store = context.global.Deno.env.get(key);
            break;
        case CONTEXT.GoogleAppsScript:
            store = context.global.PropertiesService?.getUserProperties().getProperty(key);
            break;
        default:
            throw new Error(`Cannot determine Javascript context: ${context.type}`);
    }
    return isString(store)
        ? objectify(store) // rebuild object from its stringified representation
        : dflt;
}
/**
 * Sets or deletes a value in the active storage mechanism across any runtime environment.
 * Automatically serializes objects for safe storage.
 *
 * @param key - The storage key to set
 * @param val - The value to store (if undefined, the key is deleted)
 * @example
 * ```ts
 * setStorage('user', { name: 'Alice' });
 * setStorage('user', undefined); // deletes 'user'
 * ```
 */
export function setStorage(key, val) {
    const stash = isDefined(val) ? stringify(val) : undefined;
    const set = isDefined(stash);
    switch (context.type) {
        case CONTEXT.Browser:
            set
                ? storage.setItem(key, stash)
                : storage.removeItem(key);
            break;
        case CONTEXT.NodeJS:
            set
                ? (context.global.process.env[key] = stash)
                : (delete context.global.process.env[key]);
            break;
        case CONTEXT.Deno:
            set
                ? context.global.Deno.env.set(key, stash)
                : context.global.Deno.env.delete(key);
            break;
        case CONTEXT.GoogleAppsScript:
            set
                ? context.global.PropertiesService?.getUserProperties().setProperty(key, stash)
                : context.global.PropertiesService?.getUserProperties().deleteProperty(key);
            break;
        default:
            throw new Error(`Cannot determine Javascript context: ${context.type}`);
    }
}
